import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { openConfirmModal } from '@weezevent/nacre';

export function useInterval(callback, delay) {
    const intervalRef = React.useRef(null);
    const callbackRef = React.useRef(callback);

    React.useEffect(() => {
        callbackRef.current = callback;
    });

    React.useEffect(() => {
        function call() {
            callbackRef.current();
        }
        if (typeof delay === 'number') {
            intervalRef.current = setInterval(call, delay);
            return () => clearInterval(intervalRef.current);
        }
    }, [delay]);
    return intervalRef;
}

export function useMousePosition() {
    const mousePosition = React.useRef({
        x: 0,
        y: 0
    });

    let mouseListener = React.useCallback(({ x, y }) => {
        mousePosition.current = { x, y };
    }, []);

    React.useEffect(() => {
        window.addEventListener('mousemove', mouseListener);
        return () => window.removeEventListener('mousemove', mouseListener);
    }, []);

    return mousePosition;
}

export function useUnsavedChangesWarning(isDirty) {
    const history = useHistory();
    const { t } = useTranslation();

    const unblockRef = React.useRef(null);
    const isPromptOpen = React.useRef(false);

    React.useEffect(() => {
        if (!isDirty) return;

        const handleBeforeUnload = e => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        let unblocking = false;
        const unblock = history.block(location => {
            if (unblocking) return true;
            if (isPromptOpen.current) return false;

            const promptUser = async () => {
                isPromptOpen.current = true;

                const confirmed = await openConfirmModal({
                    title: t('ticket.common.unsaved_changes_warning'),
                    subtitle: t('ticket.common.unsaved_changes_subtitle'),
                    confirm: t('ticket.common.action.quit_without_saving'),
                    cancel: t('ticket.common.action.cancel'),
                    destructive: true
                });

                isPromptOpen.current = false;

                if (confirmed) {
                    unblocking = true;
                    unblock();
                    history.push(location.pathname);
                }
            };

            promptUser();
            return false;
        });

        unblockRef.current = unblock;

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            if (unblockRef.current) {
                unblockRef.current();
                unblockRef.current = null;
            }
        };
    }, [isDirty, history, t]);

    const allowNavigation = React.useCallback(() => {
        if (unblockRef.current) {
            unblockRef.current();
            unblockRef.current = null;
        }
    }, []);

    return allowNavigation;
}
